import { useDebounceCallback } from 'custom_hooks/useDebounceCallback';
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
    Bar,
    BarProps,
    CartesianGrid,
    LabelListProps,
    Tooltip,
    TooltipContentProps,
    XAxis,
    YAxis,
} from 'recharts';
import styled from 'styled-components';
import { tokens } from 'styles/tokens';
import { assertNumber } from 'utils/assert';

import { StyledBarChart } from './styled';

/**
 * Configuration for a single segment in the stacked bar chart.
 * Multiple segments with the same stackId are stacked horizontally.
 */
export type SegmentConfig<
    TData,
    TKey extends string & keyof TData = string & keyof TData
> = {
    dataKey: TKey;
    label: string;
    color: string;
    textColor?: string;
    stackId: string;
};

type Props<
    TData extends Record<string, unknown>,
    TCategoryKey extends string & keyof TData
> = Readonly<{
    data: TData[];
    segments: SegmentConfig<TData>[];
    /** Key for Y-axis category labels */
    categoryKey: TCategoryKey;
    yAxisWidth?: number;
    xAxisHeight?: number;
    minBarHeight?: string;
    /** Spacing between bar groups (not segments within a stack) */
    barCategoryGap?: number;
    showGrid?: boolean;
    showTooltip?: boolean;
    /** Show numeric value labels centered in each bar segment */
    showBarLabels?: boolean;
    /** Horizontal gap in pixels between segments within a stack */
    stackGap?: 0 | 1 | 2 | 4 | 8;
    /** Corner rounding: 'none', 'last-item' (last visible segment), or 'both' (first and last edges) */
    rounding?: 'none' | 'last-item' | 'both';
    cornerRadius?: number;
    /** Minimum value required to display a label on a bar segment (default 'auto') */
    labelMinValue?: number | `${number}%` | 'auto' | 'none';
    /** Stack offset type for diverging bar charts */
    stackOffset?: 'none' | 'expand' | 'wiggle' | 'silhouette' | 'sign';
    /** Custom formatter for X-axis tick values */
    xAxisTickFormatter?: (value: number) => string;
    /** Custom formatter for tooltip values */
    /** Preprocessor for cleaning/transforming values before display (e.g., Math.abs for removing negatives) */
    cleanValue?: (value: number) => number;
}>;

const BarsContainer = styled.div`
    .recharts-layer .recharts-label-list text.recharts-text {
        fill: ${tokens.text.primary};
        font-weight: 600;
    }
`;

/**
 * Creates an SVG path for a rectangle with optional rounded corners on the right side.
 * @param x - X coordinate of the top-left corner
 * @param y - Y coordinate of the top-left corner
 * @param width - Width of the rectangle
 * @param height - Height of the rectangle
 * @param radius - Radius for the rounded corners (default: 5)
 * @returns SVG path string
 */
function createRightRoundedRectPath(
    x: number,
    y: number,
    width: number,
    height: number,
    radius: number = 5
): string {
    // M = move to
    // L = line to
    // Q = quadratic bezier curve: start (current position), control point, end point (https://developer.mozilla.org/en-US/docs/Web/SVG/Reference/Attribute/d#quadratic_b%C3%A9zier_curve)
    // Z = close shape

    // M  top-left
    // L top right inner
    // Q curve to top far-right
    // L line to bottom far-right
    // Q curve to bottom right
    // L line to bottom left
    // Z close shape with starting point
    return `
        M ${x} ${y}
        L ${x + width - radius} ${y}
        Q ${x + width} ${y}, ${x + width} ${y + radius}
        L ${x + width} ${y + height - radius}
        Q ${x + width} ${y + height}, ${x + width - radius} ${y + height}
        L ${x} ${y + height}
        Z
    `;
}

/**
 * Creates an SVG path for a rectangle with optional rounded corners on the left side.
 * Used for negative values in diverging bar charts.
 * @param x - X coordinate of the top-left corner
 * @param y - Y coordinate of the top-left corner
 * @param width - Width of the rectangle
 * @param height - Height of the rectangle
 * @param radius - Radius for the rounded corners (default: 5)
 * @returns SVG path string
 */
function createLeftRoundedRectPath(
    x: number,
    y: number,
    width: number,
    height: number,
    radius: number = 5
): string {
    // M top-left inner
    // Q curve to top far-left
    // L line to bottom far-left
    // Q curve to bottom left inner
    // L line to bottom right
    // L line to top right
    // Z close shape
    return `
        M ${x + radius} ${y}
        Q ${x} ${y}, ${x} ${y + radius}
        L ${x} ${y + height - radius}
        Q ${x} ${y + height}, ${x + radius} ${y + height}
        L ${x + width} ${y + height}
        L ${x + width} ${y}
        Z
    `;
}

/**
 * Creates label configuration for stacked bars
 */
function createStackedBarLabelConfig(
    labelMinValue: number,
    textColor?: string,
    cleanValue?: (value: number) => number
): LabelListProps {
    return {
        position: 'center' as const,
        ...(textColor && { style: { fill: textColor } }),
        valueAccessor: ({ value }: { value: unknown }) => {
            if (typeof value === 'number') {
                return value;
            } else if (
                Array.isArray(value) &&
                typeof value[0] === 'number' &&
                typeof value[1] === 'number'
            ) {
                return value[1] - value[0];
            }
            return Number(value);
        },
        formatter: cleanValue
            ? (item: unknown): number | undefined => {
                  const numValue = Number(item);
                  return Math.abs(numValue) >= labelMinValue
                      ? cleanValue(numValue)
                      : undefined;
              }
            : (item: unknown): number | undefined => {
                  const numValue = Number(item);
                  return Math.abs(numValue) >= labelMinValue ? numValue : undefined;
              },
        fontSize: 11,
    };
}

/**
 * Checks if any segment in a range has a non-zero value.
 */
function hasAnySegmentInRange<TData extends Record<string, unknown>>(
    rowData: TData,
    segments: SegmentConfig<TData>[],
    startIdx: number,
    endIdx: number
): boolean {
    for (let i = startIdx; i < endIdx; i++) {
        const value = rowData[segments[i].dataKey];
        if (value !== null && value !== undefined && value !== 0) {
            return true;
        }
    }
    return false;
}

/**
 * Determines if and how a bar segment should have rounded corners.
 * Returns: false for no rounding, 'left' for left edge only, 'right' for right edge only, 'both' for all corners
 */
function shouldRoundBarSegment<TData extends Record<string, unknown>>(
    rounding: 'none' | 'last-item' | 'both',
    data: TData[],
    segments: SegmentConfig<TData>[],
    idxInStack: number,
    dataIndex: number | undefined
): false | 'left' | 'right' | 'both' {
    if (rounding === 'none') {
        return false;
    }

    const rowData = typeof dataIndex === 'number' ? data[dataIndex] : undefined;
    if (!rowData) {
        return false;
    }

    const currentSegment = segments[idxInStack];
    const value = rowData[currentSegment.dataKey];
    if (!value) {
        return false;
    }

    if (rounding === 'both') {
        // Check if this is the first/last visible segment
        const isFirstVisible = !hasAnySegmentInRange(rowData, segments, 0, idxInStack);
        const isLastVisible = !hasAnySegmentInRange(
            rowData,
            segments,
            idxInStack + 1,
            segments.length
        );

        if (isFirstVisible && isLastVisible) {
            return 'both';
        }
        if (isFirstVisible) {
            return 'left';
        }
        if (isLastVisible) {
            return 'right';
        }
        return false;
    }

    // rounding === 'last-item'
    // Check if this is the last visible segment
    return hasAnySegmentInRange(rowData, segments, idxInStack + 1, segments.length)
        ? false
        : 'right';
}

/**
 * Creates the appropriate rounded bar shape (rect or path) based on rounding requirements.
 */
function createRoundedBarShape(
    roundingType: 'left' | 'right' | 'both',
    fill: string | undefined,
    x: number,
    y: number,
    width: number,
    height: number,
    radius: number
): JSX.Element {
    switch (roundingType) {
        case 'both':
            return (
                <rect
                    fill={fill}
                    x={x}
                    y={y}
                    width={width}
                    height={height}
                    rx={radius}
                    ry={radius}
                />
            );
        case 'left':
            return (
                <path
                    d={createLeftRoundedRectPath(x, y, width, height, radius)}
                    fill={fill}
                />
            );
        case 'right':
            return (
                <path
                    d={createRightRoundedRectPath(x, y, width, height, radius)}
                    fill={fill}
                />
            );
    }
}

/**
 * Creates shape renderer for stacked bars with optional right-rounded corners
 * and stack gaps. Supports diverging bars with stackOffset="sign".
 */
function createStackedBarShapeRenderer<TData extends Record<string, unknown>>(
    data: TData[],
    segments: SegmentConfig<TData>[],
    idxInStack: number,
    options: {
        rounding: 'none' | 'last-item' | 'both';
        useStackGap: boolean;
        stackGap: number;
        cornerRadius: number;
    }
) {
    return (props: BarProps) => {
        const { fill, x, y, width, height, index } = props;
        assertNumber(x);
        assertNumber(width);
        assertNumber(y);
        assertNumber(height);

        // don't render anything for zero values
        if (width === 0) {
            return <></>;
        }

        const adjustedX = options.useStackGap ? x + options.stackGap / 2 : x;
        const adjustedWidth = options.useStackGap ? width - options.stackGap : width;

        const roundingType = shouldRoundBarSegment(
            options.rounding,
            data,
            segments,
            idxInStack,
            Number(index)
        );

        if (roundingType) {
            // Handle negative widths: x is the right edge, so left edge is x + width
            const shapeX = adjustedWidth < 0 ? adjustedX + adjustedWidth : adjustedX;
            const shapeWidth = Math.abs(adjustedWidth);

            return createRoundedBarShape(
                roundingType,
                fill,
                shapeX,
                y,
                shapeWidth,
                height,
                options.cornerRadius
            );
        }

        // for non-rounded segments, use simple rect
        return (
            <rect fill={fill} x={adjustedX} y={y} width={adjustedWidth} height={height} />
        );
    };
}

const CustomTooltipWrapper = styled.div`
    background-color: ${tokens.background.default};
    border: 1px solid ${tokens.border.default};
    padding-inline: 1rem;
    padding-block: 1rem;
    border-radius: 5px;
`;

export const CustomTooltip = ({
    active,
    payload,
    label,
    cleanValue,
}: TooltipContentProps<string | number, number | string> & {
    cleanValue?: (value: number) => number;
}) => {
    if (active && payload.length) {
        return (
            <CustomTooltipWrapper className="custom-tooltip">
                <span className="label">{`${label}`}</span>
                <div>
                    {payload.map((pld) => (
                        <div
                            key={pld.dataKey}
                            className="flex flex row align-center gap-4"
                        >
                            <div
                                style={{
                                    width: '14px',
                                    height: '14px',
                                    borderRadius: '3px',
                                    backgroundColor: pld.fill,
                                }}
                            />
                            <span>
                                <span style={{ color: tokens.text.secondary }}>
                                    {pld.dataKey}:
                                </span>
                                &nbsp;
                                <span>
                                    {cleanValue ? cleanValue(pld.value) : pld.value}
                                </span>
                            </span>
                        </div>
                    ))}
                </div>
            </CustomTooltipWrapper>
        );
    }

    return null;
};

export function StackedHorizontalBarChart<
    TData extends Record<string, unknown>,
    TCategoryKey extends string & keyof TData
>({
    data,
    segments,
    categoryKey,
    yAxisWidth = 90,
    xAxisHeight = 30,
    barCategoryGap = 4,
    showGrid = true,
    showTooltip = true,
    showBarLabels = false,
    stackGap = 0,
    rounding = 'none',
    cornerRadius = 5,
    labelMinValue: _labelMinValue = 'auto',
    stackOffset,
    xAxisTickFormatter,
    cleanValue,
}: Props<TData, TCategoryKey>) {
    const useRoundedCorners = rounding !== 'none';
    const useStackGap = stackGap > 0;

    const [isAnimationActive, setIsAnimationActive] = useState(true);

    const labelMinValue = useMemo(() => {
        const computeMinValueFromBarLen = (percentFrac: number) => {
            const maxBarLen = Math.max(
                ...data.flatMap((d) => segments.map((seg) => Number(d[seg.dataKey])))
            );
            return percentFrac * maxBarLen;
        };
        switch (_labelMinValue) {
            case 'auto': {
                return computeMinValueFromBarLen(0.05);
            }
            case 'none':
                return 0;
            default: {
                if (typeof _labelMinValue === 'string') {
                    const percentFrac =
                        Number(_labelMinValue.substring(0, _labelMinValue.length - 1)) /
                        100;
                    return computeMinValueFromBarLen(percentFrac);
                } else {
                    return _labelMinValue;
                }
            }
        }
    }, [_labelMinValue, data, segments]);

    // see [LABELLIST-ANIMATIONS]
    const disableAnimationsAfterDone = useDebounceCallback(2000, () =>
        setIsAnimationActive(false)
    );
    const lastDataRef = useRef<TData[] | null>(null);
    useLayoutEffect(() => {
        if (lastDataRef.current !== data) {
            lastDataRef.current = data;
            setIsAnimationActive(true);
        }
        disableAnimationsAfterDone();
    }, [data, disableAnimationsAfterDone]);

    const ChartTooltip = useCallback(
        (props: TooltipContentProps<string | number, number | string>) => (
            <CustomTooltip {...props} cleanValue={cleanValue} />
        ),
        [cleanValue]
    );

    return (
        <BarsContainer>
            <StyledBarChart
                data={data}
                layout="vertical"
                barCategoryGap={barCategoryGap}
                stackOffset={stackOffset}
            >
                {showGrid && (
                    <CartesianGrid
                        strokeDasharray="3 3"
                        stroke={tokens.border.default}
                        horizontal={false}
                    />
                )}
                <XAxis
                    type="number"
                    tick={{ fill: tokens.text.primary }}
                    height={xAxisHeight}
                    tickFormatter={xAxisTickFormatter}
                />
                <YAxis
                    type="category"
                    dataKey={categoryKey}
                    tick={{ fill: tokens.text.primary }}
                    width={yAxisWidth}
                />
                {showTooltip && (
                    <Tooltip
                        contentStyle={{
                            backgroundColor: tokens.background.default,
                            border: `1px solid ${tokens.border.default}`,
                        }}
                        labelStyle={{ color: tokens.text.primary }}
                        labelFormatter={(l) => l + 'abc'}
                        itemStyle={{ display: 'flex', color: tokens.text.primary }}
                        content={ChartTooltip}
                        itemSorter={() => 1} // https://github.com/recharts/recharts/issues/1241#issuecomment-477157979
                    />
                )}
                {segments.map((segment, idxInStack) => {
                    const shapeRenderer =
                        useRoundedCorners || useStackGap
                            ? createStackedBarShapeRenderer(data, segments, idxInStack, {
                                  rounding,
                                  useStackGap,
                                  stackGap,
                                  cornerRadius,
                              })
                            : undefined;

                    const segmentLabelConfig = showBarLabels
                        ? createStackedBarLabelConfig(
                              labelMinValue,
                              segment.textColor,
                              cleanValue
                          )
                        : undefined;

                    return (
                        <Bar
                            key={segment.dataKey}
                            dataKey={segment.dataKey}
                            stackId={segment.stackId}
                            fill={segment.color}
                            name={segment.label}
                            label={segmentLabelConfig}
                            shape={shapeRenderer}
                            // [LABELLIST-ANIMATIONS]
                            // this stops the LabelList from flashing on scroll.
                            // If just set to `true`, it remove all bar animations,
                            // thus the "allow animations for 2 seconds after new data"
                            // state nonsense
                            // https://github.com/recharts/recharts/issues/1478
                            // https://github.com/recharts/recharts/issues/829
                            isAnimationActive={isAnimationActive}
                        />
                    );
                })}
            </StyledBarChart>
        </BarsContainer>
    );
}
